import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  ClockIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FunnelIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilSquareIcon,
  ClipboardDocumentCheckIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { addDays, differenceInCalendarDays, format, startOfWeek, endOfWeek, parseISO } from "date-fns";
import { updateHourAsync } from "../../../slices/hour";
import { fetchHours } from "../../../api/hour";
import { fetchWorkOrders, isPromangApiErrorResponse } from "../../../api/work-order";
import axios from "../../../api/axios";
import { dateToString, UserTitle, dispatchWithToast } from "../../Utils";
import { ApproveHourDialog } from "../hours/approveHourDialog";
import { HourEditDialog } from "../hours/hour_edit_dialog";
import { HoursBulkReassignDialog } from "../hours/hours_bulk_reassign_dialog";
import { HourBillingButton, useCanReconcileHours } from "../hours/HourBillingDialog";
import { PeriodSelector } from "../hours/period_selector";
import { PMDialog } from "../../utils/PMDialog";
import { normalizeError } from "../../../operationFeedback/normalizeError";
import { OUTCOME } from "../../../operationFeedback/types";
import { useCanBulkReassignHours, useProjectAccountingAccess, useUserHasAccess } from "../../../routes/roleOnly";
import { useProjectActivityPickerData } from "../../utils/moduleSelectors/useProjectActivityPickerData";
import { projectActivityLine } from "../hours/projectActivityDisplay";
import {
  mergeProjectTimeOverviewSearchParams,
  mergeRouterAndWindowSearchParams,
  parseProjectTimeOverviewSearchParams,
  rangeForPeriod,
} from "./projectTimeOverviewSearchParams";
import {
  enrichHourWithActivity,
  sumHours,
} from "./projectTimeOverviewUtils";
import { ProjectTimeFilterPicker } from "./ProjectTimeFilterPicker";
import { useProjectHoursPdfExport } from "./useProjectHoursPdfExport";
import {
  activityIdOf, countProjectTimeFacets, filterProjectHours, groupProjectHours, hoursOf, orderIdOf,
  projectHoursCsv, projectIdOf, departmentIdOf, statusOf, summarizeProjectHours, supplierInvoiceIdOf, uncoveredSupplierHoursOf,
} from "./projectTimeOverviewData";
import "./project-time-overview.css";
import { intlLocaleFor } from "../../../i18n/languages";

const BASE_HOUR_FIELDS = "id,time,date,description,process_note,user,project,project_activity,project_activity_id,work_order,billing,status,approved,not_approved,processed";
const COST_HOUR_FIELDS = `${BASE_HOUR_FIELDS},effective_hourly_rate,effective_hour_cost_rate`;

export const ProjectTimeList = ({
  project,
  companyScope = false,
  canManageProjectTime = false,
  restrictHoursToCurrentUserId = null,
  includeHourlyCosts = false,
  theme = "light",
  onEntriesChange,
  onLoadStateChange,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n?.language === "en" ? "en-US" : intlLocaleFor(i18n?.language);
  const ui = useCallback((key, defaultValue, values = {}) =>
    t(`project.time_overview.ui.${key}`, { defaultValue, ...values }), [t]);
  const hourUnit = ui("hour_unit", "t");
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFilters = useMemo(
    () => parseProjectTimeOverviewSearchParams(searchParams),
    [searchParams]
  );
  const canBulkReassign = useCanBulkReassignHours();
  // The process endpoint requires a company admin, even though the older UI
  // useCanApproveHours hook also grants the hours_approval role.
  const canApprove = useUserHasAccess({ role: "admin" });
  const canReadSupplierInvoices = useProjectAccountingAccess();
  const canOpenInvoiceDetail = useUserHasAccess({ role: "accounting", action: "read" });
  const canReconcile = useCanReconcileHours();
  const canSelectHours = canReconcile || (canManageProjectTime && (canBulkReassign || canApprove));
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [expandedRowGroups, setExpandedRowGroups] = useState(new Set());
  const [showAllUngrouped, setShowAllUngrouped] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [view, setView] = useState("list");
  const [groupBy, setGroupBy] = useState(companyScope ? "project" : "order");
  const [matrixBy, setMatrixBy] = useState("user");
  const [compactRows, setCompactRows] = useState(false);
  const [showWeekends, setShowWeekends] = useState(false);
  const [query, setQuery] = useState("");
  const [userIds, setUserIds] = useState([]);
  const [projectIds, setProjectIds] = useState([]);
  const [departmentIds, setDepartmentIds] = useState([]);
  const [activityIds, setActivityIds] = useState([]);
  const [orderIds, setOrderIds] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [types, setTypes] = useState([]);
  const [links, setLinks] = useState([]);
  const [newHourOpen, setNewHourOpen] = useState(false);
  const [newHourPrefill, setNewHourPrefill] = useState(null);
  const [showAllHours, setShowAllHours] = useState(urlFilters.all);
  const [periodType, setPeriodType] = useState(urlFilters.period);
  // In "All hours" mode the URL carries no range; keep a real period (the one
  // the user returns to via the selector) instead of a from = to anchor date.
  const [periodFrom, setPeriodFrom] = useState(
    () => urlFilters.dateFrom || rangeForPeriod(urlFilters.period, urlFilters.date).dateFrom
  );
  const [periodTo, setPeriodTo] = useState(
    () => urlFilters.dateTo || rangeForPeriod(urlFilters.period, urlFilters.date).dateTo
  );
  const { activities: projectActivities } =
    useProjectActivityPickerData(project?.id, {
      enabled: Boolean(project?.id),
    });
  const activitiesById = useMemo(() => {
    const map = new Map();
    (projectActivities || []).forEach((row) => {
      if (row?.id != null) map.set(String(row.id), row);
    });
    return map;
  }, [projectActivities]);

  const writeUrlFilters = useCallback(
    (patch) => {
      setSearchParams(
        (prev) =>
          mergeProjectTimeOverviewSearchParams(
            mergeRouterAndWindowSearchParams(prev),
            patch
          ),
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const [processDialogHour, setProcessDialogHour] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editHourId, setEditHourId] = useState(null);
  const [selectedHourIds, setSelectedHourIds] = useState(new Set());
  const [bulkReassignOpen, setBulkReassignOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkNote, setBulkNote] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkFeedback, setBulkFeedback] = useState("");
  const [bulkFeedbackError, setBulkFeedbackError] = useState(false);
  const [bulkComplete, setBulkComplete] = useState(false);
  const hourFiltersForProject = useCallback(() => {
    const f = [];
    if (project?.id) f.push({ name: "PROJECT", value: project.id });
    if (
      restrictHoursToCurrentUserId != null &&
      restrictHoursToCurrentUserId !== ""
    ) {
      f.push({ name: "USER", value: restrictHoursToCurrentUserId });
    }
    return f;
  }, [project?.id, restrictHoursToCurrentUserId]);

  const [controller, setController] = useState(() => ({
    query: "",
    page: 1,
    per_page: 100,
    sortBy: "date",
    sort: "desc",
    filters: (() => {
      const f = [];
      if (project?.id) f.push({ name: "PROJECT", value: project.id });
      if (
        restrictHoursToCurrentUserId != null &&
        restrictHoursToCurrentUserId !== ""
      ) {
        f.push({ name: "USER", value: restrictHoursToCurrentUserId });
      }
      return f;
    })(),
    date_from: urlFilters.dateFrom,
    date_to: urlFilters.dateTo,
    fields: includeHourlyCosts ? COST_HOUR_FIELDS : BASE_HOUR_FIELDS,
  }));

  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [revision, setRevision] = useState(0);
  const [projectInvoices, setProjectInvoices] = useState({});
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceLoadError, setInvoiceLoadError] = useState(false);
  const [invoiceRetry, setInvoiceRetry] = useState(0);

  useEffect(() => {
    const fields = includeHourlyCosts ? COST_HOUR_FIELDS : BASE_HOUR_FIELDS;
    setController((previous) => previous.fields === fields ? previous : { ...previous, fields });
  }, [includeHourlyCosts]);

  useEffect(() => {
    if (!project?.id || !canReadSupplierInvoices) {
      setProjectInvoices({});
      setInvoiceLoading(false);
      setInvoiceLoadError(false);
      return undefined;
    }
    let active = true;
    setProjectInvoices({});
    setInvoiceLoading(true);
    setInvoiceLoadError(false);
    axios.get(`/projects/${project.id}/supplier_invoices`)
      .then((response) => {
        if (!response?.data?.success || !Array.isArray(response.data.data)) throw new Error("Invalid invoice response");
        if (active) setProjectInvoices(Object.fromEntries(response.data.data.map((invoice) => [String(invoice.id), invoice])));
      })
      .catch(() => { if (active) setInvoiceLoadError(true); })
      .finally(() => { if (active) setInvoiceLoading(false); });
    return () => { active = false; };
  }, [project?.id, canReadSupplierInvoices, invoiceRetry]);

  useEffect(() => {
    if (!project?.id && !companyScope) return undefined;
    let active = true;
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      onLoadStateChange?.("loading");
      try {
        // The API paginates /hours. Summary cards, costs and CSV must cover the
        // entire selected period, including projects with more than 100 entries.
        const first = await fetchHours({ ...controller, page: 1 });
        if (!Array.isArray(first?.data)) throw new Error("Kunne ikke laste timer.");
        const total = Number(first?.totalCount) || 0;
        const perPage = Number(controller.per_page) || 100;
        const pages = Math.ceil(total / perPage);
        const rows = [...(first?.data || [])];
        for (let start = 2; start <= pages; start += 5) {
          const batch = await Promise.all(
            Array.from({ length: Math.min(5, pages - start + 1) }, (_, index) =>
              fetchHours({ ...controller, page: start + index })
            )
          );
        batch.forEach((result) => {
          if (!Array.isArray(result?.data)) throw new Error("Kunne ikke laste alle timer.");
          rows.push(...result.data);
        });
        }
        if (active) {
          setTimeEntries(rows);
          onLoadStateChange?.("ready");
        }
      } catch (error) {
        if (active) {
          setTimeEntries([]);
          onLoadStateChange?.("error");
          setLoadError(error);
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [controller, onLoadStateChange, project?.id, companyScope, revision]);

  useEffect(() => {
    setSelectedHourIds(new Set());
    setController((prev) => {
      const filters = hourFiltersForProject();
      // Unchanged filters (e.g. on mount) must not trigger a second /hours load.
      if (JSON.stringify(prev.filters) === JSON.stringify(filters)) return prev;
      return { ...prev, filters };
    });
  }, [project?.id, hourFiltersForProject]);

  useEffect(() => {
    setSelectedHourIds(new Set());
    setShowAllHours(urlFilters.all);
    setPeriodType(urlFilters.period);
    if (urlFilters.dateFrom) setPeriodFrom(urlFilters.dateFrom);
    if (urlFilters.dateTo) setPeriodTo(urlFilters.dateTo);
    setController((prev) => {
      if (
        prev.date_from === urlFilters.dateFrom &&
        prev.date_to === urlFilters.dateTo
      ) {
        return prev;
      }
      return {
        ...prev,
        page: 1,
        date_from: urlFilters.dateFrom,
        date_to: urlFilters.dateTo,
      };
    });
  }, [urlFilters.all, urlFilters.period, urlFilters.dateFrom, urlFilters.dateTo]);

  const noActivity = t("project.time_registration.no_activity");

  const enrichedEntries = useMemo(
    () => (timeEntries || []).map((entry) => enrichHourWithActivity(entry, activitiesById)),
    [timeEntries, activitiesById]
  );
  const [orderStats, setOrderStats] = useState({});
  const visibleOrderIds = useMemo(() => [...new Set(enrichedEntries.map(orderIdOf).filter((id) => id != null))], [enrichedEntries]);
  useEffect(() => {
    if (!canManageProjectTime || restrictHoursToCurrentUserId != null || !visibleOrderIds.length) {
      setOrderStats({});
      return undefined;
    }
    let active = true;
    const loadOrderStats = async () => {
      const stats = {};
      try {
        for (let start = 0; start < visibleOrderIds.length; start += 50) {
          const response = await fetchWorkOrders({
            ids: visibleOrderIds.slice(start, start + 50).join(","),
            fields: "id,budgeted_hours,hours", per_page: 50, page: 1,
          });
          if (isPromangApiErrorResponse(response) || !Array.isArray(response?.data?.data)) break;
          response.data.data.forEach((order) => {
            stats[String(order.id)] = {
              budget: Number(order.budgeted_hours) || 0,
              logged: sumHours(order.hours || []),
            };
          });
        }
      } catch (_error) {
        // Hour cards still show the selected-period total if order details are unavailable.
      }
      if (active) setOrderStats(stats);
    };
    loadOrderStats();
    return () => { active = false; };
  }, [canManageProjectTime, restrictHoursToCurrentUserId, visibleOrderIds]);

  const filteredEntries = useMemo(() => filterProjectHours(enrichedEntries, {
    query, userIds, projectIds, departmentIds, activityIds, orderIds, statuses, types, links, locale,
  }), [enrichedEntries, query, userIds, projectIds, departmentIds, activityIds, orderIds, statuses, types, links, locale]);

  // Parent cost panel must track the visible (chip-filtered) rows, same as PDF/CSV.
  useEffect(() => {
    if (loading) {
      onEntriesChange?.([]);
      return;
    }
    onEntriesChange?.(filteredEntries);
  }, [filteredEntries, loading, onEntriesChange]);

  const facetCounts = useMemo(() => countProjectTimeFacets(enrichedEntries, {
    query, userIds, projectIds, departmentIds, activityIds, orderIds, statuses, types, links, locale,
  }), [enrichedEntries, query, userIds, projectIds, departmentIds, activityIds, orderIds, statuses, types, links, locale]);
  useEffect(() => {
    setSelectedHourIds(new Set());
  }, [query, userIds, projectIds, departmentIds, activityIds, orderIds, statuses, types, links]);
  const groupLabels = useMemo(() => ({
    locale,
    unknownEmployee: ui("unknown_employee", "Ukjent ansatt"),
    withoutProject: ui("without_project", "Uten prosjekt"),
    withoutActivity: ui("without_activity", "Uten aktivitet"),
    withoutDate: ui("without_date", "Uten dato"),
    withoutOrder: ui("without_order", "Uten ordre"),
    order: ui("order", "Ordre"),
    approved: ui("status_approved", "Godkjent"),
    rejected: ui("status_rejected", "Avvist"),
    processed: ui("status_processed", "Behandlet"),
    pending: ui("status_pending", "Til godkjenning"),
  }), [ui, locale]);
  const groupedEntries = useMemo(() => groupProjectHours(filteredEntries, groupBy, groupLabels), [filteredEntries, groupBy, groupLabels]);
  const filteredSummary = useMemo(() => summarizeProjectHours(filteredEntries), [filteredEntries]);
  const orderCardEntries = useMemo(() => filterProjectHours(enrichedEntries, {
    query, userIds, activityIds, statuses, types, links, locale,
  }), [enrichedEntries, query, userIds, activityIds, statuses, types, links, locale]);
  const budgetHours = (projectActivities || []).reduce((sum, row) => sum + (Number(row?.budget_hours) || 0), 0);
  const loggedAllHours = (projectActivities || []).reduce((sum, row) => sum + (Number(row?.logged_hours) || 0), 0);
  const teamCount = Array.isArray(project?.participants)
    ? new Set(project.participants.filter((member) => member?.status !== "COMPLETED")
      .map((member) => member?.user?.id).filter((id) => id != null)).size
    : null;
  const optionCounts = useMemo(() => {
    const users = new Map();
    const projects = new Map();
    const departments = new Map();
    const activities = new Map();
    const orders = new Map();
    const add = (map, value, label) => {
      const key = String(value ?? "none");
      if (!map.has(key)) map.set(key, { value: key, label });
    };
    enrichedEntries.forEach((entry) => {
      add(users, entry?.user?.id, entry?.user?.displayTitle || groupLabels.unknownEmployee);
      add(projects, projectIdOf(entry), entry?.project?.displayTitle || entry?.project?.name || groupLabels.withoutProject);
      add(departments, departmentIdOf(entry), entry?.user?.department?.displayTitle || entry?.user?.department?.title || ui("without_department", "Uten avdeling"));
      add(activities, activityIdOf(entry), entry?.project_activity?.display_title || entry?.project_activity?.title || groupLabels.withoutActivity);
      add(orders, orderIdOf(entry), entry?.work_order?.displayTitle || entry?.work_order?.title || groupLabels.withoutOrder);
    });
    // Offer every project activity, not only those with hours in the loaded
    // period, so several activities can be picked (values are OR-ed).
    (projectActivities || []).forEach((row) => {
      if (row?.id != null) add(activities, row.id, projectActivityLine(row) || `#${row.id}`);
    });
    // Options with matches first, then alphabetical. Counts of a facet ignore
    // its own selection, so the order stays stable while toggling values.
    const sort = (map, counts) => [...map.values()].map((option) => ({ ...option, count: counts[option.value] || 0 }))
      .sort((a, b) => (b.count > 0) - (a.count > 0) || a.label.localeCompare(b.label, locale));
    return { users: sort(users, facetCounts.users), projects: sort(projects, facetCounts.projects),
      departments: sort(departments, facetCounts.departments),
      activities: sort(activities, facetCounts.activities),
      orders: sort(orders, facetCounts.orders) };
  }, [enrichedEntries, projectActivities, facetCounts, groupLabels, locale, ui]);

  const selectedIds = enrichedEntries.filter((entry) => selectedHourIds.has(entry.id)).map((entry) => entry.id);
  const selectedPendingEntries = enrichedEntries.filter((entry) => selectedHourIds.has(entry.id) && statusOf(entry) === "pending");

  const refreshHours = useCallback(() => setRevision((value) => value + 1), []);

  const processSelected = async () => {
    if (!canManageProjectTime || !canApprove || !bulkAction || !selectedPendingEntries.length || bulkBusy || bulkComplete) return;
    if (bulkAction === "reject" && !bulkNote.trim()) return;
    setBulkBusy(true);
    setBulkFeedback("");
    setBulkFeedbackError(false);
    let completed = 0;
    let remaining = [];
    let errorMessage = "";
    for (let index = 0; index < selectedPendingEntries.length; index += 1) {
      const entry = selectedPendingEntries[index];
      try {
        await dispatch(updateHourAsync({
          id: entry.id, process: true,
          approved: bulkAction === "approve", not_approved: bulkAction === "reject",
          process_note: bulkAction === "reject" ? bulkNote.trim() : entry.process_note || "",
        })).unwrap();
        completed += 1;
      } catch (error) {
        const normalized = normalizeError(error);
        errorMessage = normalized.outcome === OUTCOME.NOT_APPLIED
          ? normalized.safeMessage
          : ui("bulk_unknown_outcome", "Utfallet av siste føring er ukjent. Kontroller status før du prøver igjen.");
        remaining = selectedPendingEntries.slice(index).map((row) => row.id);
        break;
      }
    }
    refreshHours();
    setSelectedHourIds(new Set(remaining));
    setBulkBusy(false);
    setBulkComplete(true);
    setBulkFeedbackError(remaining.length > 0);
    setBulkFeedback(remaining.length
      ? ui("bulk_remaining", "{{done}} bekreftet behandlet. {{remaining}} gjenstår. {{error}}",
        { done: completed, remaining: remaining.length, error: errorMessage })
      : ui(bulkAction === "approve" ? "bulk_approved" : "bulk_rejected",
        bulkAction === "approve" ? "{{count}} føringer godkjent." : "{{count}} føringer avvist.", { count: completed }));
  };

  const processHourQuick = useCallback(
    (hour, approved) => {
      dispatchWithToast(
        dispatch(
          updateHourAsync({
            id: hour.id,
            process: true,
            approved,
            not_approved: !approved,
            process_note: hour.process_note || "",
          })
        ),
        refreshHours
      );
    },
    [dispatch, refreshHours]
  );

  const openHourEditDialog = useCallback((entry) => {
    if (!canManageProjectTime || entry?.id == null) return;
    setEditHourId(String(entry.id));
    setEditDialogOpen(true);
  }, [canManageProjectTime]);

  const toggleHourSelected = useCallback((entryId) => {
    setSelectedHourIds((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  }, []);

  const toggleHoursSelected = useCallback((entries) => {
    setSelectedHourIds((prev) => {
      const next = new Set(prev);
      const ids = entries.map((entry) => entry.id);
      if (ids.every((id) => next.has(id))) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const closeHourEditDialog = useCallback(() => {
    setEditDialogOpen(false);
    setEditHourId(null);
  }, []);

  const handleHourSaved = useCallback(() => {
    refreshHours();
    closeHourEditDialog();
    setNewHourOpen(false);
  }, [refreshHours, closeHourEditDialog]);

  const handleHourDeleted = useCallback(() => {
    refreshHours();
    closeHourEditDialog();
  }, [refreshHours, closeHourEditDialog]);

  const handlePeriodChange = useCallback(({ periodType: nextType, from, to }) => {
    setSelectedHourIds(new Set());
    const nextPeriod = nextType || "month";
    setShowAllHours(false);
    setPeriodType(nextPeriod);
    setPeriodFrom(from);
    setPeriodTo(to);
    writeUrlFilters({
      all: false,
      period: nextPeriod,
      date: from,
      to: nextPeriod === "custom" ? to : null,
    });
    setController((prev) => ({
      ...prev,
      page: 1,
      date_from: from,
      date_to: to,
    }));
  }, [writeUrlFilters]);

  const handleShowAllHours = useCallback(() => {
    setSelectedHourIds(new Set());
    setShowAllHours(true);
    writeUrlFilters({ all: true });
    setController((prev) => ({
      ...prev,
      page: 1,
      date_from: null,
      date_to: null,
    }));
  }, [writeUrlFilters]);

  const companyExportFilters = useMemo(() => {
    const filters = [];
    projectIds.forEach((id) => filters.push({ name: "PROJECT", value: id }));
    userIds.forEach((id) => filters.push({ name: "USER", value: id }));
    departmentIds.forEach((id) => filters.push({ name: "DEPARTMENT", value: id }));
    activityIds.forEach((id) => filters.push({ name: "ACTIVITY", value: id }));
    orderIds.forEach((id) => filters.push({ name: "WORK_ORDER", value: id }));
    return filters;
  }, [projectIds, userIds, departmentIds, activityIds, orderIds]);

  // PDF is rendered by the API from the currently filtered entries (project and
  // company overview both send visible hour ids so client-only chips match).
  const hoursPdf = useProjectHoursPdfExport({
    projectId: project?.id, projectNumber: project?.project_number, entries: filteredEntries, dateFrom: controller.date_from || null,
    dateTo: controller.date_to || null, language: i18n?.language, ui,
    companyScope, exportFilters: companyExportFilters,
  });

  const downloadCsv = useCallback(() => {
    const csv = projectHoursCsv(filteredEntries, { locale, includeBilling: canReconcile, labels: {
      date: ui("date", "Dato"), employee: ui("employee", "Ansatt"), type: ui("type", "Type"),
      activity: ui("activity", "Aktivitet"), order: ui("order", "Ordre"),
      supplierInvoice: ui("supplier_invoice", "Leverandørfaktura"), hours: ui("hours", "Timer"),
      status: ui("status", "Status"), description: ui("description_column", "Beskrivelse"),
      supplier: ui("supplier", "Underleverandør"), approved: ui("status_approved", "Godkjent"),
      rejected: ui("status_rejected", "Avvist"), processed: ui("status_processed", "Behandlet"),
      pending: ui("status_pending", "Til godkjenning"),
    } });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    const scopeName = project?.project_number || project?.id || "selskap";
    anchor.download = `timer-${scopeName}-${periodFrom || "alle"}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }, [filteredEntries, project?.id, project?.project_number, periodFrom, locale, ui, canReconcile]);

  const matrix = useMemo(() => {
    const dated = filteredEntries.filter((entry) => /^\d{4}-\d{2}-\d{2}$/.test(String(entry.date || "")));
    if (!dated.length) return { columns: [], rows: [], weekly: false };
    const dates = dated.map((entry) => String(entry.date)).sort();
    const from = showAllHours ? dates[0] : periodFrom || dates[0];
    const to = showAllHours ? dates[dates.length - 1] : periodTo || dates[dates.length - 1];
    const weekly = differenceInCalendarDays(parseISO(to), parseISO(from)) > 31;
    const columns = [];
    let cursor = weekly ? startOfWeek(parseISO(from), { weekStartsOn: 1 }) : parseISO(from);
    const end = parseISO(to);
    while (cursor <= end) {
      const start = format(cursor, "yyyy-MM-dd");
      const stop = weekly ? format(endOfWeek(cursor, { weekStartsOn: 1 }), "yyyy-MM-dd") : start;
      if (weekly || showWeekends || (cursor.getDay() !== 0 && cursor.getDay() !== 6)) {
        columns.push({ start, stop, label: weekly ? ui("week_number", "Uke {{number}}", { number: format(cursor, "I") }) : format(cursor, "dd.MM") });
      }
      cursor = addDays(cursor, weekly ? 7 : 1);
    }
    const rows = groupProjectHours(dated, matrixBy, groupLabels).map((group) => {
      // Row/footer SUM must match visible columns only — when weekends are
      // hidden, Saturday/Sunday stay out of both cells and the totals.
      const cells = columns.map(({ start, stop }) => group.entries
        .filter((entry) => entry.date >= start && entry.date <= stop)
        .reduce((sum, entry) => sum + hoursOf(entry), 0));
      return {
        ...group,
        cells,
        hours: cells.reduce((sum, value) => sum + value, 0),
      };
    });
    return { columns, rows, weekly };
  }, [filteredEntries, matrixBy, periodFrom, periodTo, showAllHours, showWeekends, groupLabels, ui]);

  const approvalBadge = (entry) => {
    if (entry.approved) {
      return (
        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
          {t("project.time_registration.badge_approved")}
        </span>
      );
    }
    if (entry.not_approved) {
      return (
        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
          {t("project.time_registration.badge_rejected")}
        </span>
      );
    }
    if (entry.processed) {
      return (
        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
          {ui("status_processed", "Behandlet")}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
        {t("project.time_registration.badge_pending")}
      </span>
    );
  };

  const renderEntryRow = (entry) => (
    <div
      key={entry.id}
      className={`pto-row${canManageProjectTime ? " is-editable" : ""}`}
      onClick={canManageProjectTime ? () => openHourEditDialog(entry) : undefined}
      role={canManageProjectTime ? "button" : undefined}
      tabIndex={canManageProjectTime ? 0 : undefined}
      onKeyDown={canManageProjectTime ? (event) => {
        if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          openHourEditDialog(entry);
        }
      } : undefined}
    >
      <div className="pto-cell pto-cell--date">
        {canSelectHours && (
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-indigo-600"
            checked={selectedHourIds.has(entry.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleHourSelected(entry.id)}
            aria-label={t("project.time_registration.select_hour")}
          />
        )}
        <span>{dateToString(entry.date)}</span>
      </div>
      <div className="pto-cell pto-cell--user"><UserTitle user={entry.user} /></div>
      {companyScope ? <div className="pto-cell pto-cell--project">
        {entry.project?.displayTitle || entry.project?.name || <span className="pto-missing">{ui("without_project", "Uten prosjekt")}</span>}
      </div> : null}
      <div className="pto-cell pto-cell--activity">
        <strong>{entry.project_activity?.display_title || entry.project_activity?.title || noActivity}</strong>
        {entry.description ? <small title={entry.description}>{entry.description}</small> : null}
      </div>
      <div className="pto-cell pto-cell--order">
        {entry.work_order?.number || entry.work_order?.displayTitle || entry.work_order?.title ||
          <span className="pto-missing">{ui("without_order", "Uten ordre")}</span>}
      </div>
      {canReconcile ? <div className="pto-cell pto-cell--invoice">
        {supplierInvoiceIdOf(entry) ? `#${supplierInvoiceIdOf(entry)}` : entry.billing?.hired ? ui("not_linked", "Ikke koblet") : "—"}
      </div> : null}
      <strong className="pto-cell pto-cell--hours">{hoursOf(entry).toLocaleString(locale, { maximumFractionDigits: 1 })} {hourUnit}</strong>
      <div className={`pto-cell pto-cell--status is-${statusOf(entry)}`}>{approvalBadge(entry)}</div>
      <div className="pto-cell pto-cell--actions">
        {canManageProjectTime && (
          <div
            className="flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            {canApprove && statusOf(entry) === "pending" && (
              <>
                <button
                  type="button"
                  title={t("project.time_registration.approve")}
                  className="rounded p-1 text-green-600 hover:bg-green-50"
                  onClick={() => processHourQuick(entry, true)}
                >
                  <CheckCircleIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  title={t("project.time_registration.reject")}
                  className="rounded p-1 text-red-600 hover:bg-red-50"
                  onClick={() => processHourQuick(entry, false)}
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              </>
            )}
            <button
              type="button"
              title={t("project.time_registration.process_dialog")}
              className="rounded p-1 text-gray-600 hover:bg-gray-100"
              onClick={() => setProcessDialogHour(entry)}
            >
              <ClipboardDocumentCheckIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              title={t("project.time_registration.open_hour")}
              className="rounded p-1 text-indigo-600 hover:bg-indigo-50"
              onClick={() => openHourEditDialog(entry)}
            >
              <PencilSquareIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderGroupedHours = () => groupedEntries.map((group) => {
      const collapsed = collapsedGroups.has(group.key);
      const expanded = expandedRowGroups.has(group.key);
      const visibleRows = expanded ? group.entries : group.entries.slice(0, 60);
      const allSelected = group.entries.every((entry) => selectedHourIds.has(entry.id));
      return <section className="pto-group" key={`${groupBy}:${group.key}`}>
        <div className="pto-group-header">
          {canSelectHours ? <input type="checkbox" aria-label={ui("select_group", "Velg gruppe {{title}}", { title: group.title })}
            checked={allSelected} onChange={() => toggleHoursSelected(group.entries)} /> : null}
          <button type="button" aria-expanded={!collapsed}
            onClick={() => setCollapsedGroups((prev) => {
              const next = new Set(prev);
              if (next.has(group.key)) next.delete(group.key);
              else next.add(group.key);
              return next;
            })}>
            {collapsed ? <ChevronRightIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
            <span>{group.title}</span><small>{ui("entries_count", "{{count}} føringer", { count: group.entries.length })}</small>
            <small>{ui("hours_waiting", "{{hours}} t venter", { hours: sumHours(group.entries.filter((entry) => statusOf(entry) === "pending"))
              .toLocaleString(locale, { maximumFractionDigits: 1 }) })}</small>
            <strong>{group.hours.toLocaleString(locale, { maximumFractionDigits: 1 })} {hourUnit}</strong>
          </button>
        </div>
        {!collapsed ? <>
          {visibleRows.map(renderEntryRow)}
          {!expanded && group.entries.length > visibleRows.length ? <button type="button" className="pto-show-more"
            onClick={() => setExpandedRowGroups((prev) => new Set(prev).add(group.key))}>
            {ui("show_more", "Vis {{count}} flere føringer", { count: group.entries.length - visibleRows.length })}
          </button> : null}
        </> : null}
      </section>;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600" />
        <span className="ml-2 text-gray-600">
          {t("project.time_overview.loading")}
        </span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-5 text-red-900">
        <p>{t("project.time_overview.load_error", "Kunne ikke hente timene for perioden.")}</p>
        <button type="button" className="mt-3 rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-semibold" onClick={refreshHours}>
          {t("common.retry")}
        </button>
      </div>
    );
  }

  const hasAny = filteredEntries.length > 0;
  const number = (value) => Number(value || 0).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const orderCards = groupProjectHours(orderCardEntries, "order", groupLabels);
  const filterChips = [
    ...(query ? [{ label: ui("search_chip", "Søk: {{query}}", { query }), remove: () => setQuery("") }] : []),
    ...[[userIds, optionCounts.users, setUserIds], [projectIds, optionCounts.projects, setProjectIds],
      [departmentIds, optionCounts.departments, setDepartmentIds],
      [activityIds, optionCounts.activities, setActivityIds],
      [orderIds, optionCounts.orders, setOrderIds]].flatMap(([values, options, setter]) =>
      values.map((value) => ({ label: options.find((option) => option.value === value)?.label || value,
        remove: () => setter(values.filter((item) => item !== value)) }))),
    ...statuses.map((value) => ({ label: ({ pending: ui("status_pending", "Til godkjenning"), approved: ui("status_approved", "Godkjent"),
      rejected: ui("status_rejected", "Avvist"), processed: ui("status_processed", "Behandlet") })[value] || value,
      remove: () => setStatuses(statuses.filter((item) => item !== value)) })),
    ...types.map((value) => ({ label: value === "supplier" ? ui("supplier", "Underleverandør") : ui("employees", "Egne ansatte"),
      remove: () => setTypes(types.filter((item) => item !== value)) })),
    ...links.map((value) => ({ label: ({ order: ui("with_order", "Med ordre"), "no-order": ui("without_order", "Uten ordre"),
      invoice: ui("with_invoice", "Med leverandørfaktura"), "no-invoice": ui("without_full_invoice", "Uten full fakturadekning") })[value] || value,
      remove: () => setLinks(links.filter((item) => item !== value)) })),
  ];
  const invoiceHourGroups = filteredEntries.reduce((groups, entry) => {
    if (!entry.billing?.hired) return groups;
    const key = supplierInvoiceIdOf(entry) == null ? "unlinked" : String(supplierInvoiceIdOf(entry));
    (groups[key] ||= []).push(entry);
    return groups;
  }, {});
  // The project endpoint also includes booked invoices that have no hour links.
  // Keep them visible instead of treating the filtered hour list as the invoice ledger.
  const invoiceIds = [...new Set([...Object.keys(projectInvoices), ...Object.keys(invoiceHourGroups)])]
    .sort((a, b) => {
      if (a === "unlinked") return 1;
      if (b === "unlinked") return -1;
      return String(projectInvoices[b]?.invoice_date || "").localeCompare(String(projectInvoices[a]?.invoice_date || ""));
    });

  return (
    <div className={`pto${compactRows ? " pto--compact" : ""}`} data-theme={theme === "dark" ? "dark" : "light"}>
      <div className="pto-metrics">
        {budgetHours > 0 ? <div className="pto-metric"><span>{ui("time_budget", "TIMEBUDSJETT")}</span><strong>{number(loggedAllHours)} / {number(budgetHours)} {hourUnit}</strong>
          <div className="pto-order-progress" role="progressbar" aria-label={ui("budget_used", "Timebudsjett brukt")}
            aria-valuenow={Math.min(100, Math.round(loggedAllHours / budgetHours * 100))} aria-valuemin={0} aria-valuemax={100}>
            <span style={{ width: `${Math.min(100, loggedAllHours / budgetHours * 100)}%` }} />
          </div><small>{ui("percent_used", "{{percent}} % brukt", { percent: Math.round(loggedAllHours / budgetHours * 100) })}</small>
        </div> : null}
        {budgetHours > 0 ? <div className="pto-metric"><span>{ui("budget_remaining", "GJENSTÅR AV BUDSJETT")}</span>
          <strong>{number(Math.max(0, budgetHours - loggedAllHours))} {hourUnit}</strong>
          <small>{loggedAllHours > budgetHours ? ui("over_budget", "{{hours}} t over budsjett", { hours: number(loggedAllHours - budgetHours) })
            : ui("available_budget", "Tilgjengelige budsjetttimer")}</small>
        </div> : null}
        <div className="pto-metric"><span>{ui("registered_selected", "REGISTRERT I UTVALGET")}</span><strong>{number(filteredSummary.total)} {hourUnit}</strong>
          <small>{ui("entry_people_count", "{{entries}} føringer · {{people}} personer", { entries: filteredSummary.count, people: filteredSummary.people })}</small></div>
        {includeHourlyCosts ? <div className="pto-metric"><span>{ui("estimated_cost_upper", "ESTIMERT KOSTNAD")}</span>
          <strong>{filteredEntries.reduce((sum, entry) => sum + hoursOf(entry) * (Number(entry.effective_hour_cost_rate) || 0), 0)
            .toLocaleString(locale, { maximumFractionDigits: 0 })} NOK</strong>
          <small>{ui("estimated_from_rates", "Timekostnad × timer i utvalget")}</small></div> : null}
        {includeHourlyCosts ? <div className="pto-metric"><span>{ui("estimated_sales_upper", "ESTIMERT SALG")}</span>
          <strong>{filteredEntries.reduce((sum, entry) => sum + hoursOf(entry) * (Number(entry.effective_hourly_rate) || 0), 0)
            .toLocaleString(locale, { maximumFractionDigits: 0 })} NOK</strong>
          <small>{ui("estimated_from_sales_rates", "Timepris × timer i utvalget")}</small></div> : null}
        {teamCount != null ? <div className="pto-metric"><span>{ui("team", "TEAM")}</span><strong>{teamCount}</strong><small>{ui("active_members", "aktive prosjektdeltakere")}</small></div> : null}
        <button type="button" className="pto-metric" onClick={() => { setStatuses(["pending"]); setView("list"); }}>
          <span>{ui("awaiting_approval", "TIL GODKJENNING")}</span><strong>{number(filteredSummary.pending)} {hourUnit}</strong>
          <small>{ui("entries_waiting", "{{count}} føringer venter", { count: filteredSummary.pendingCount })}</small>
        </button>
        <button type="button" className="pto-metric" onClick={() => { setOrderIds(["none"]); setView("list"); }}>
          <span>{ui("without_order_upper", "UTEN ORDRE")}</span><strong>{number(filteredSummary.withoutOrder)} {hourUnit}</strong>
          <small>{ui("entries_count", "{{count}} føringer", { count: filteredSummary.withoutOrderCount })}</small>
        </button>
        {canReconcile && filteredSummary.supplier > 0 ? <button type="button" className="pto-metric" onClick={() => { setLinks(["no-invoice"]); setView("list"); }}>
          <span>{ui("supplier_upper", "UNDERLEVERANDØR")}</span><strong>{number(filteredSummary.supplier)} {hourUnit}</strong>
          <small>{ui("hours_without_full_invoice", "{{hours}} t uten full fakturadekning", { hours: number(filteredSummary.supplierUnlinked) })}</small>
        </button> : null}
      </div>

      <section className="pto-panel">
        <div className="pto-panel-title"><span className="pto-eyebrow">{ui("hours_per_order", "TIMER PER ORDRE · VALGT PERIODE")}</span>
          <small>{ui("select_order_hint", "Velg en ordre for å filtrere føringene")}</small></div>
        <div className="pto-order-grid">{orderCards.map((order) => <button type="button" key={order.key}
          className={`pto-order-card${orderIds.includes(order.key) ? " is-selected" : ""}`}
          onClick={() => { setOrderIds(orderIds.includes(order.key) ? [] : [order.key]); setView("list"); }}>
          <span>{order.key === "none" ? ui("without_order_upper", "UTEN ORDRE") : order.entries[0]?.work_order?.number || `#${order.key}`}</span>
          <strong>{order.title}</strong><b>{number(order.hours)} {hourUnit} <small>{ui("in_period", "i perioden")}</small></b>
          <small>{ui("hours_waiting", "{{hours}} t venter", { hours: number(sumHours(order.entries.filter((entry) => statusOf(entry) === "pending"))) })}</small>
          {orderStats[order.key]?.budget > 0 ? <>
            <div className="pto-order-progress" role="progressbar" aria-label={ui("order_budget_used", "Budsjett brukt for {{order}}", { order: order.title })}
              aria-valuenow={Math.min(100, Math.round(orderStats[order.key].logged / orderStats[order.key].budget * 100))}
              aria-valuemin={0} aria-valuemax={100}>
              <span style={{ width: `${Math.min(100, orderStats[order.key].logged / orderStats[order.key].budget * 100)}%`,
                background: orderStats[order.key].logged > orderStats[order.key].budget ? "#e54848" : "var(--pto-accent)" }} />
            </div>
            <small>{ui("order_budget_total", "{{used}} av {{budget}} t totalt · {{percent}} %", {
              used: number(orderStats[order.key].logged), budget: number(orderStats[order.key].budget),
              percent: Math.round(orderStats[order.key].logged / orderStats[order.key].budget * 100),
            })}</small>
          </> : null}
        </button>)}</div>
      </section>

      <section className="pto-panel pto-controls">
        <div className="pto-filter-bar">
          <div className={`pto-period${showAllHours ? " is-inactive" : ""}`}>
            <PeriodSelector
              periodType={periodType}
              from={periodFrom}
              to={periodTo}
              onChange={handlePeriodChange}
              summaryLabel={showAllHours ? t("project.time_registration.all_hours") : undefined}
              dialogPanelClassName={theme === "dark" ? "pto-period-dialog--dark" : ""}
            />
          </div>
          <button type="button" onClick={handleShowAllHours} aria-pressed={showAllHours}
            className={`pm-btn pm-btn--secondary pto-all-hours${showAllHours ? " is-selected" : ""}`}>
            {t("project.time_registration.all_hours")}
          </button>
          <label className="pto-search"><MagnifyingGlassIcon className="h-4 w-4" /><input type="search" aria-label={ui("search_hours", "Søk i timer")}
            placeholder={ui("search_placeholder", "Søk kommentar, ordre, ansatt")} value={query} onChange={(event) => setQuery(event.target.value)} /></label>
          <button type="button" className="pto-button pto-filter-toggle" aria-expanded={showFilters} onClick={() => setShowFilters(!showFilters)}><FunnelIcon className="h-4 w-4" />{ui("filters", "Filtre")}</button>
          {showFilters ? <>
            {companyScope ? <>
              <ProjectTimeFilterPicker label={ui("project", "Prosjekt")} options={optionCounts.projects} selected={projectIds} onChange={setProjectIds} dark={theme === "dark"} />
              <ProjectTimeFilterPicker label={ui("department", "Avdeling")} options={optionCounts.departments} selected={departmentIds} onChange={setDepartmentIds} dark={theme === "dark"} />
            </> : null}
            <ProjectTimeFilterPicker label={ui("activity", "Aktivitet")} options={optionCounts.activities} selected={activityIds} onChange={setActivityIds} dark={theme === "dark"} />
            <ProjectTimeFilterPicker label={ui("order", "Ordre")} options={optionCounts.orders} selected={orderIds} onChange={setOrderIds} dark={theme === "dark"} />
            <ProjectTimeFilterPicker label={ui("employee", "Ansatt")} options={optionCounts.users} selected={userIds} onChange={setUserIds} dark={theme === "dark"} />
            <ProjectTimeFilterPicker label={ui("status", "Status")} options={[
              { value: "pending", label: ui("status_pending", "Til godkjenning"), count: facetCounts.statuses.pending || 0 },
              { value: "approved", label: ui("status_approved", "Godkjent"), count: facetCounts.statuses.approved || 0 },
              { value: "rejected", label: ui("status_rejected", "Avvist"), count: facetCounts.statuses.rejected || 0 },
              { value: "processed", label: ui("status_processed", "Behandlet"), count: facetCounts.statuses.processed || 0 },
            ]} selected={statuses} onChange={setStatuses} dark={theme === "dark"} />
            {canReconcile ? <ProjectTimeFilterPicker label={ui("type", "Type")} options={[
              { value: "employee", label: ui("employees", "Egne ansatte"), count: facetCounts.types.employee || 0 },
              { value: "supplier", label: ui("supplier", "Underleverandør"), count: facetCounts.types.supplier || 0 },
            ]} selected={types} onChange={setTypes} dark={theme === "dark"} /> : null}
            <ProjectTimeFilterPicker label={ui("link", "Kobling")} options={[
              { value: "order", label: ui("with_order", "Med ordre"), count: facetCounts.links.order },
              { value: "no-order", label: ui("without_order", "Uten ordre"), count: facetCounts.links["no-order"] },
              ...(canReconcile ? [
                { value: "invoice", label: ui("with_invoice", "Med leverandørfaktura"), count: facetCounts.links.invoice },
                { value: "no-invoice", label: ui("without_full_invoice", "Uten full fakturadekning"), count: facetCounts.links["no-invoice"] },
              ] : []),
            ]} selected={links} onChange={setLinks} dark={theme === "dark"} />
          </> : null}
          <div className="pto-filter-actions">
            <button type="button" className="pto-button" onClick={downloadCsv}><ArrowDownTrayIcon className="h-4 w-4" />{ui("export_csv", "Eksporter CSV")}</button>
            <button type="button" className="pto-button" onClick={hoursPdf.exportPdf} disabled={(!project?.id && !companyScope) || hoursPdf.busy || loading}
              aria-busy={hoursPdf.busy}><ArrowDownTrayIcon className="h-4 w-4" />{hoursPdf.busy ? ui("export_pdf_busy", "Lager PDF…") : ui("export_pdf", "Eksporter PDF")}</button>
            {hoursPdf.feedback ? <p role="alert" className="pto-pdf-feedback">{hoursPdf.feedback.message}
              {hoursPdf.feedback.recovery === "reload" ? <> <button type="button" onClick={() => { hoursPdf.clearFeedback(); refreshHours(); }}>
                {ui("pdf_refresh_list", "Oppdater listen")}</button></> : null}</p> : null}
            <button type="button" className="pto-button pto-button--primary" onClick={() => {
              setNewHourPrefill({ project_id: project?.id, project, date: format(new Date(), "yyyy-MM-dd") });
              setNewHourOpen(true);
            }}><PlusIcon className="h-4 w-4" />{ui("new_entry", "Ny føring")}</button>
          </div>
        </div>
        {filterChips.length ? <div className="pto-filter-chips">
          {filterChips.map((chip, index) => <button type="button" key={`${chip.label}-${index}`} onClick={chip.remove}
            aria-label={ui("remove_filter", "Fjern filter: {{label}}", { label: chip.label })}>
            {chip.label}<span aria-hidden="true">×</span>
          </button>)}
          <button type="button" className="pto-clear" onClick={() => { setQuery(""); setUserIds([]); setProjectIds([]); setDepartmentIds([]); setActivityIds([]); setOrderIds([]); setStatuses([]); setTypes([]); setLinks([]); }}>
            {ui("reset_all", "Nullstill alle · {{filtered}} av {{total}} føringer", { filtered: filteredEntries.length, total: enrichedEntries.length })}
          </button>
        </div> : null}
      </section>

      <section className="pto-panel pto-results">
        <div className="pto-tabs" role="tablist" aria-label={ui("time_view", "Timevisning")}>
          <button role="tab" type="button" aria-selected={view === "list"} onClick={() => setView("list")}>{ui("entries", "Føringer")} <span>{filteredEntries.length}</span></button>
          <button role="tab" type="button" aria-selected={view === "matrix"} onClick={() => setView("matrix")}>{ui("matrix", "Matrise")}</button>
          {canReconcile || canReadSupplierInvoices ? <button role="tab" type="button" aria-selected={view === "invoices"} onClick={() => setView("invoices")}>{ui("supplier_invoice", "Leverandørfaktura")}</button> : null}
        </div>

        {view === "list" ? <div role="tabpanel" className="pto-list">
          <div className="pto-list-toolbar"><span className="pto-eyebrow">{ui("groups", "GRUPPER")}</span>
            {[ ...(companyScope ? [["project", ui("project", "Prosjekt")]] : []),
              ["order", ui("order", "Ordre")], ["user", ui("employee", "Ansatt")], ["activity", ui("activity", "Aktivitet")],
              ["date", ui("date", "Dato")], ["status", ui("status", "Status")], ["none", ui("none", "Ingen")] ].map(([key, label]) =>
              <button type="button" key={key} className={groupBy === key ? "is-active" : ""} onClick={() => { setGroupBy(key); setCollapsedGroups(new Set()); setExpandedRowGroups(new Set()); setShowAllUngrouped(false); }}>{label}</button>)}
            {groupBy !== "none" && groupedEntries.length ? <button type="button" onClick={() =>
              setCollapsedGroups(collapsedGroups.size === groupedEntries.length ? new Set() : new Set(groupedEntries.map((group) => group.key)))}>
              {collapsedGroups.size === groupedEntries.length ? ui("expand_all", "Utvid alle") : ui("collapse_all", "Slå sammen alle")}
            </button> : null}
            <button type="button" className={compactRows ? "is-active" : ""} aria-pressed={compactRows}
              onClick={() => setCompactRows((value) => !value)}>{compactRows ? ui("normal_rows", "Normale rader") : ui("compact_rows", "Kompakte rader")}</button>
          </div>
          {canSelectHours ? <div className="pto-selection">
            <span>{selectedIds.length ? ui("selected_hours", "{{count}} valgt · {{hours}} t", {
              count: selectedIds.length, hours: number(sumHours(enrichedEntries.filter((entry) => selectedHourIds.has(entry.id)))) })
              : ui("select_for_bulk", "Velg føringer for massehandlinger")}</span>
            {canManageProjectTime && canApprove && selectedPendingEntries.length ? <>
              <button type="button" className="pto-button" onClick={() => { setBulkAction("approve"); setBulkFeedback(""); setBulkComplete(false); }}>
                {ui("approve_selected", "Godkjenn valgte ({{count}})", { count: selectedPendingEntries.length })}</button>
              <button type="button" className="pto-button" onClick={() => { setBulkAction("reject"); setBulkFeedback(""); setBulkComplete(false); }}>
                {ui("reject_selected", "Avvis valgte ({{count}})", { count: selectedPendingEntries.length })}</button>
            </> : null}
            <HourBillingButton hourIds={selectedIds} onSaved={() => { setSelectedHourIds(new Set()); refreshHours(); }} />
            {canManageProjectTime && canBulkReassign && selectedIds.length ? <button type="button" className="pto-button" onClick={() => setBulkReassignOpen(true)}>{t("project.time_registration.bulk_reassign")}</button> : null}
            {selectedIds.length ? <button type="button" className="pto-button" onClick={() => setSelectedHourIds(new Set())}>{ui("reset", "Nullstill")}</button> : null}
          </div> : null}
          <div className="pto-table-scroll"><div className={`pto-table${canReconcile ? " has-billing" : ""}${companyScope ? " has-project" : ""}`}>
            <div className="pto-table-head"><span>{canSelectHours ? <input type="checkbox" aria-label={ui("select_all", "Velg alle føringer i utvalget")}
              checked={filteredEntries.length > 0 && filteredEntries.every((entry) => selectedHourIds.has(entry.id))}
              disabled={!filteredEntries.length} onChange={() => toggleHoursSelected(filteredEntries)} /> : null} {ui("date_upper", "DATO")}</span>
              <span>{ui("employee_upper", "ANSATT")}</span>
              {companyScope ? <span>{ui("project_upper", "PROSJEKT")}</span> : null}
              <span>{ui("activity_upper", "AKTIVITET")}</span><span>{ui("order_upper", "ORDRE")}</span>
              {canReconcile ? <span>{ui("invoice_upper", "LEV.FAKTURA")}</span> : null}<span>{ui("hours_upper", "TIMER")}</span>
              <span>{ui("status_upper", "STATUS")}</span><span>{ui("actions_upper", "HANDLINGER")}</span></div>
            {!hasAny ? <div className="pto-empty"><ClockIcon className="h-8 w-8" /><p>{t("project.time_overview.empty")}</p></div> :
              groupBy === "none" ? <>{(showAllUngrouped ? filteredEntries : filteredEntries.slice(0, 150)).map(renderEntryRow)}
                {!showAllUngrouped && filteredEntries.length > 150 ? <button type="button" className="pto-show-more" onClick={() => setShowAllUngrouped(true)}>
                  {ui("show_more", "Vis {{count}} flere føringer", { count: filteredEntries.length - 150 })}
                </button> : null}</> : renderGroupedHours()}
            <div className="pto-table-foot"><span>{ui("sum_entries", "SUM · {{count}} FØRINGER", { count: filteredSummary.count })}</span>
              <strong>{number(filteredSummary.total)} {hourUnit}</strong></div>
          </div></div>
        </div> : null}

        {view === "matrix" ? <div role="tabpanel" className="pto-matrix">
          <div className="pto-list-toolbar"><span className="pto-eyebrow">{ui("rows", "RADER")}</span>
            {[ ["user", ui("employee", "Ansatt")], ["activity", ui("activity", "Aktivitet")], ["order", ui("order", "Ordre")] ].map(([key, label]) =>
              <button type="button" key={key} className={matrixBy === key ? "is-active" : ""} onClick={() => setMatrixBy(key)}>{label}</button>)}
            {!matrix.weekly ? <button type="button" className={showWeekends ? "is-active" : ""} aria-pressed={showWeekends}
              onClick={() => setShowWeekends((value) => !value)}>{showWeekends ? ui("hide_weekends", "Skjul helger") : ui("show_weekends", "Vis helger")}</button> : null}
            <small>{ui("matrix_hint", "{{period}} · klikk en celle for å se føringene", {
              period: matrix.weekly ? ui("weeks", "Uker") : ui("days", "Dager"),
            })}</small>
          </div>
          {matrix.rows.length ? <div className="pto-matrix-scroll"><table><thead><tr><th>{matrixBy === "user" ? ui("employee_upper", "ANSATT") : matrixBy === "order" ? ui("order_upper", "ORDRE") : ui("activity_upper", "AKTIVITET")}</th>
            {matrix.columns.map((column) => <th key={column.start}>{column.label}</th>)}<th>{ui("sum", "SUM")}</th></tr></thead><tbody>
            {matrix.rows.map((row) => <tr key={row.key}><th scope="row">{row.title}</th>{row.cells.map((value, index) => <td key={matrix.columns[index].start}>
              {value ? <button type="button" onClick={() => {
                setView("list");
                if (matrixBy === "user") setUserIds([row.key]);
                if (matrixBy === "activity") setActivityIds([row.key]);
                if (matrixBy === "order") setOrderIds([row.key]);
                handlePeriodChange({ periodType: "custom", from: matrix.columns[index].start, to: matrix.columns[index].stop });
              }}>{number(value)}</button> : "—"}</td>)}<td><strong>{number(row.hours)}</strong></td></tr>)}
          </tbody><tfoot><tr><th scope="row">{ui("sum", "SUM")}</th>{matrix.columns.map((column, index) =>
            <td key={column.start}><strong>{number(matrix.rows.reduce((sum, row) => sum + row.cells[index], 0))}</strong></td>)}
            <td><strong>{number(matrix.rows.reduce((sum, row) => sum + row.hours, 0))}</strong></td></tr></tfoot></table></div> : <div className="pto-empty">{ui("no_hours_period", "Ingen timer i perioden.")}</div>}
        </div> : null}

        {view === "invoices" && (canReconcile || canReadSupplierInvoices) ? <div role="tabpanel" className="pto-invoices">
          <p>{canReconcile
            ? ui("invoice_intro", "Prosjektets registrerte leverandørfakturaer og fakturadekning for timene i valgt periode. Velg timer i «Føringer» for å koble eller avstemme.")
            : ui("invoice_intro_readonly", "Prosjektets registrerte leverandørfakturaer. Timekoblinger krever tilgang til timeavstemming.")}</p>
          {invoiceLoading ? <p role="status">{ui("loading_invoices", "Henter prosjektets leverandørfakturaer…")}</p> : null}
          {invoiceLoadError ? <div role="alert">
            <p>{canReconcile
              ? ui("invoice_details_error", "Fakturadetaljene kunne ikke hentes. Timekoblinger og fakturadekning vises fortsatt.")
              : ui("invoice_readonly_error", "Leverandørfakturaene kunne ikke hentes. Prøv igjen.")}</p>
            <button type="button" className="pto-button" onClick={() => setInvoiceRetry((value) => value + 1)}>{t("common.retry")}</button>
          </div> : null}
          {invoiceIds.map((invoiceId) => {
            const rows = invoiceHourGroups[invoiceId] || [];
            const invoice = projectInvoices[invoiceId];
            const covered = rows.reduce((total, entry) => total + (Number(entry.billing?.invoice_hours) || 0), 0);
            const uncovered = rows.reduce((total, entry) => total + uncoveredSupplierHoursOf(entry), 0);
            const lines = Object.values(rows.reduce((groups, entry) => {
              const key = `${entry.user?.id ?? "none"}|${orderIdOf(entry) ?? "none"}`;
              if (!groups[key]) groups[key] = {
                key, employee: entry.user?.displayTitle || ui("unknown_employee", "Ukjent ansatt"),
                order: entry.work_order?.number || entry.work_order?.displayTitle || ui("without_order", "Uten ordre"),
                hours: 0, covered: 0,
              };
              groups[key].hours += hoursOf(entry);
              groups[key].covered += Number(entry.billing?.invoice_hours) || 0;
              return groups;
            }, {}));
            return <div className="pto-invoice-row" key={invoiceId}>
              <div className="pto-invoice-heading">
                <span>{invoiceId === "unlinked" ? ui("not_linked_invoice", "Ikke koblet til leverandørfaktura") :
                  invoice?.invoice_number ? ui("invoice_number", "Faktura {{number}}", { number: invoice.invoice_number })
                    : ui("supplier_invoice_number", "Leverandørfaktura #{{number}}", { number: invoiceId })}</span>
                {invoice?.supplier?.name ? <small>{invoice.supplier.name}</small> : null}
                {invoice?.invoice_date ? <small>{ui("invoice_date", "Dato {{date}}", { date: dateToString(invoice.invoice_date) })}</small> : null}
                {invoice ? <small>{invoice.approved ? ui("invoice_approved_local", "Godkjent i Promang") : invoice.not_approved
                  ? ui("invoice_rejected_local", "Avvist i Promang")
                  : ui("invoice_no_local_approval", "Ingen intern godkjenning registrert")}</small> : null}
              </div>
              <div className="pto-invoice-totals">
                {invoice?.project_amount_excluding_vat != null ? <strong>{ui("project_invoice_amount", "Prosjektbeløp ekskl. mva.: {{amount}}", {
                  amount: new Intl.NumberFormat(locale, { style: "currency", currency: "NOK" }).format(Number(invoice.project_amount_excluding_vat) || 0),
                })}</strong> : null}
                {rows.length ? <>
                  <small>{ui("entries_count", "{{count}} føringer", { count: rows.length })}</small>
                  <strong>{ui("hours_logged", "{{hours}} t ført", { hours: number(sumHours(rows)) })}</strong>
                  {invoiceId !== "unlinked" ? <small>{ui("hours_covered", "{{hours}} t dekket av faktura", { hours: number(covered) })}</small> : null}
                </> : <small>{canReconcile
                  ? ui("no_linked_hours_period", "Ingen koblede timer i valgt periode")
                  : ui("hour_links_restricted", "Timekoblinger krever tilgang til timeavstemming")}</small>}
                {uncovered > 0 ? <small className="pto-invoice-gap">{ui("hours_uncovered", "{{hours}} t uten fakturadekning", { hours: number(uncovered) })}</small> : null}
              </div>
              {lines.length ? <details className="pto-invoice-details"><summary>{ui("show_hours_by_employee_order", "Vis timer per ansatt og ordre")}</summary>
                <div className="pto-invoice-lines"><div className="pto-invoice-line pto-invoice-line--head"><span>{ui("employee_upper", "ANSATT")}</span>
                  <span>{ui("order_upper", "ORDRE")}</span><span>{ui("logged_upper", "FØRT")}</span><span>{ui("covered_upper", "DEKKET")}</span></div>
                  {lines.map((line) => <div className="pto-invoice-line" key={line.key}><span>{line.employee}</span>
                    <span>{line.order}</span><span>{number(line.hours)} {hourUnit}</span><span>{number(line.covered)} {hourUnit}</span></div>)}
                </div>
              </details> : null}
              <div className="pto-invoice-actions">
                {invoiceId !== "unlinked" && canOpenInvoiceDetail ? <Link className="pv-link" to={`/dashboard/supplier-invoices/${invoiceId}`}>{ui("view_invoice", "Se faktura")}</Link> : null}
                {canReconcile && uncovered > 0 ? <HourBillingButton hourIds={rows.filter((entry) => uncoveredSupplierHoursOf(entry) > 0).map((entry) => entry.id)} onSaved={refreshHours} /> : null}
              </div>
            </div>;
          })}
          {!invoiceLoading && !invoiceLoadError && !invoiceIds.length ? <div className="pto-empty">{canReadSupplierInvoices
            ? canReconcile
              ? ui("no_supplier_invoices", "Ingen leverandørfakturaer eller underleverandørtimer i utvalget.")
              : ui("no_supplier_invoices_readonly", "Ingen leverandørfakturaer registrert for prosjektet.")
            : ui("no_supplier_hours", "Ingen underleverandørtimer i perioden.")}</div> : null}
        </div> : null}
      </section>

      <ApproveHourDialog
        key={processDialogHour?.id || "closed"}
        hour={processDialogHour}
        open={Boolean(processDialogHour)}
        onClose={() => setProcessDialogHour(null)}
        onRefresh={() => { setProcessDialogHour(null); refreshHours(); }}
      />

      <HourEditDialog
        open={editDialogOpen || newHourOpen}
        panelClassName="pto-hour-drawer"
        hourId={newHourOpen ? null : editHourId}
        prefill={newHourOpen ? newHourPrefill : null}
        onClose={() => { closeHourEditDialog(); setNewHourOpen(false); }}
        onSaved={handleHourSaved}
        onDeleted={handleHourDeleted}
      />

      <HoursBulkReassignDialog
        open={bulkReassignOpen}
        onClose={() => setBulkReassignOpen(false)}
        hourIds={selectedIds}
        initialProject={project || null}
        onDone={() => {
          setSelectedHourIds(new Set());
          refreshHours();
        }}
      />
      <PMDialog title={bulkAction === "reject" ? ui("reject_selected_hours", "Avvis valgte timer") : ui("approve_selected_hours", "Godkjenn valgte timer")}
        open={Boolean(bulkAction)} onClose={() => { if (!bulkBusy) { setBulkAction(null); setBulkFeedback(""); setBulkNote(""); setBulkComplete(false); } }}>
        <div className="pto-bulk-dialog">
          {!bulkComplete ? <p>{ui("bulk_processing", "{{count}} føringer behandles. Godkjente eller avviste føringer er ikke med.",
            { count: selectedPendingEntries.length })}</p> : null}
          {bulkAction === "reject" && !bulkComplete ? <label>{ui("rejection_reason", "Begrunnelse for avvisning")}
            <textarea rows={3} value={bulkNote} onChange={(event) => setBulkNote(event.target.value)} required />
          </label> : null}
          {bulkFeedback ? <p role={bulkFeedbackError ? "alert" : "status"}>{bulkFeedback}</p> : null}
          <div className="pto-bulk-actions">
            <button type="button" className="pto-button" disabled={bulkBusy} onClick={() => { setBulkAction(null); setBulkFeedback(""); setBulkNote(""); setBulkComplete(false); }}>
              {bulkComplete ? ui("close", "Lukk") : ui("cancel", "Avbryt")}
            </button>
            {!bulkComplete ? <button type="button" className="pto-button pto-button--primary" disabled={bulkBusy || !selectedPendingEntries.length || (bulkAction === "reject" && !bulkNote.trim())}
              onClick={processSelected}>{bulkBusy ? ui("processing", "Behandler…") : bulkAction === "reject" ? ui("reject_hours", "Avvis timer") : ui("approve_hours", "Godkjenn timer")}</button> : null}
          </div>
        </div>
      </PMDialog>
    </div>
  );
};
